import React from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Activity, Info, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Daily Business Health Score card.
 *
 * Shows a 0-100 heuristic score with a band label, sub-component bars, and
 * the single biggest opportunity ("your biggest opportunity today is
 * collections: ₹1.42L outstanding across 11 clients").
 *
 * Deliberately labelled as a "directional indicator, not an exact metric"
 * both in the header and as an info tooltip on the score number, so we
 * never overclaim what a heuristic actually represents.
 */
const BAND_META = {
  'Strong':          { pill: 'bg-emerald-100 text-emerald-800',  ring: 'text-emerald-600',  bar: 'bg-emerald-500' },
  'Steady':          { pill: 'bg-blue-100 text-blue-800',        ring: 'text-blue-600',     bar: 'bg-blue-500' },
  'Watch':           { pill: 'bg-amber-100 text-amber-800',      ring: 'text-amber-600',    bar: 'bg-amber-500' },
  'Needs attention': { pill: 'bg-red-100 text-red-800',          ring: 'text-red-600',      bar: 'bg-red-500' },
};

/**
 * Tiny SVG sparkline for the 7-day score trend. Renders a friendly "not
 * enough history yet" message if we have fewer than 2 real data points —
 * honest signal about how new the account is.
 */
const TrendSparkline = ({ points }) => {
  const list = points || [];
  const real = list.map((p, i) => ({ i, score: p.score })).filter((p) => p.score != null);
  if (real.length < 2) {
    return (
      <p className="mt-3 text-[10px] text-slate-400" data-testid="health-trend-empty">
        Trend appears here once you have 2+ days of history.
      </p>
    );
  }
  const width = 220, height = 28, pad = 2;
  const scores = real.map((p) => p.score);
  const min = Math.min(...scores, 0);
  const max = Math.max(...scores, 100);
  const range = Math.max(1, max - min);
  const stepX = (width - pad * 2) / Math.max(1, list.length - 1);
  const y = (score) => height - pad - ((score - min) / range) * (height - pad * 2);
  const path = list
    .map((p, i) => {
      if (p.score == null) return null;
      const prev = list[i - 1];
      const cmd = i === 0 || !prev || prev.score == null ? 'M' : 'L';
      return `${cmd} ${pad + i * stepX} ${y(p.score)}`;
    })
    .filter(Boolean)
    .join(' ');
  const last = real[real.length - 1];
  const first = real[0];
  const delta = last.score - first.score;
  const deltaColor = delta > 0 ? 'text-emerald-700' : delta < 0 ? 'text-red-700' : 'text-slate-500';
  const deltaSign = delta > 0 ? '+' : '';

  return (
    <div className="mt-3" data-testid="health-trend-sparkline">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider text-slate-500">7-day trend</span>
        <span className={`text-[11px] font-semibold ${deltaColor}`} data-testid="health-trend-delta">
          {deltaSign}{delta} pt{Math.abs(delta) === 1 ? '' : 's'}
        </span>
      </div>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block">
        <path d={path} fill="none" stroke="currentColor" strokeWidth="1.5" className="text-blue-600" />
        {list.map((p, i) => (p.score != null && (
          <circle key={i} cx={pad + i * stepX} cy={y(p.score)} r={2} className="fill-blue-600" />
        )))}
      </svg>
    </div>
  );
};

const HealthScoreCard = ({ data, loading, trend }) => {
  const navigate = useNavigate();
  if (loading) {
    return (
      <div
        className="rounded-xl border border-slate-200 bg-white p-4 animate-pulse"
        data-testid="health-score-loading"
      >
        <div className="h-3 w-24 bg-slate-200 rounded mb-3" />
        <div className="h-12 w-24 bg-slate-200 rounded mb-4" />
        <div className="space-y-2">
          <div className="h-2 bg-slate-200 rounded" />
          <div className="h-2 bg-slate-200 rounded w-11/12" />
          <div className="h-2 bg-slate-200 rounded w-10/12" />
        </div>
      </div>
    );
  }

  if (!data || data.score == null) return null;

  const meta = BAND_META[data.band] || BAND_META['Steady'];
  const score = data.score;
  const circumference = 2 * Math.PI * 32;
  const dashOffset = circumference * (1 - score / 100);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="health-score-card">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-blue-800" />
          <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Business Health
          </p>
        </div>
        <span
          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${meta.pill}`}
          data-testid="health-score-band"
        >
          {data.band}
        </span>
      </div>

      {/* Score dial */}
      <div className="flex items-center gap-4">
        <div className="relative shrink-0" style={{ width: 84, height: 84 }}>
          <svg width="84" height="84" viewBox="0 0 84 84" className="-rotate-90">
            <circle cx="42" cy="42" r="32" fill="none" stroke="#E2E8F0" strokeWidth="7" />
            <circle
              cx="42" cy="42" r="32"
              fill="none"
              className={meta.ring}
              stroke="currentColor"
              strokeWidth="7"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              style={{ transition: 'stroke-dashoffset 400ms ease' }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span
              className="text-2xl font-bold text-slate-900"
              style={{ fontFamily: 'Manrope' }}
              data-testid="health-score-value"
            >
              {score}
            </span>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] text-slate-500 leading-tight mb-1">Today's biggest opportunity</p>
          <p className="text-[13px] text-slate-800 leading-snug" data-testid="health-top-driver-message">
            {data.top_driver?.message || '—'}
          </p>
        </div>
      </div>

      {/* 7-day trend sparkline */}
      <TrendSparkline points={trend?.points} data-testid="health-score-trend" />

      {/* Sub-component bars — each row is clickable, deep-linking into the
          list of clients/leads/tasks behind that driver so the agent can act. */}
      <ul className="mt-3 space-y-1.5" data-testid="health-drivers-list">
        {(data.drivers || []).map((d) => (
          <li key={d.key} data-testid={`health-driver-${d.key}`}>
            <button
              type="button"
              onClick={() => d.href && navigate(d.href)}
              disabled={!d.href}
              className={`w-full text-left group ${d.href ? 'hover:bg-slate-50 rounded px-1 -mx-1 py-0.5 cursor-pointer' : ''}`}
              data-testid={`health-driver-link-${d.key}`}
            >
              <div className="flex items-center justify-between text-[11px] mb-0.5">
                <span className="text-slate-700 font-medium truncate mr-2 flex items-center gap-1">
                  {d.label}
                  {d.href && <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-70 transition-opacity" />}
                </span>
                <span className="text-slate-500 shrink-0">{d.ratio_pct}%</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded overflow-hidden">
                <div
                  className={`h-full ${
                    d.ratio_pct >= 80 ? 'bg-emerald-500' :
                    d.ratio_pct >= 60 ? 'bg-blue-500' :
                    d.ratio_pct >= 40 ? 'bg-amber-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${d.ratio_pct}%` }}
                />
              </div>
              <p className="text-[10px] text-slate-500 mt-0.5 leading-snug">{d.detail}</p>
            </button>
          </li>
        ))}
      </ul>

      <div
        className="mt-3 flex items-start gap-1.5 text-[10px] text-slate-500 border-t border-slate-100 pt-2"
        data-testid="health-score-disclaimer"
      >
        <Info className="h-3 w-3 mt-0.5 shrink-0" />
        <span>{data.disclaimer}</span>
      </div>
    </div>
  );
};

export default HealthScoreCard;